import { useState, useEffect } from 'react';
import { Trophy, Coffee, Quote, Apple } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const localDate = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const HomeView = ({ api, token, user, onStartWorkout }) => {
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/home?date=${localDate()}`, token)
      .then(setHome)
      .catch(() => setHome(null))
      .finally(() => setLoading(false));
  }, [api, token]);

  const firstName = (user.displayName || '').trim().split(' ')[0] || 'there';

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      {!loading && home?.streak >= 2 && (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-primary/40 bg-primary/10 py-2.5 text-sm font-semibold text-primary">
          <img src="/icon-192.png" alt="" className="h-5 w-5 rounded" />
          {home.streak}-workout streak — keep it rolling!
        </div>
      )}
      {loading ? (
        <Card className="mt-2"><CardContent className="py-10 text-center text-muted-foreground text-sm">Loading…</CardContent></Card>
      ) : home?.completed ? (
        <Card className="mt-2 border-primary/40">
          <CardContent className="py-7 text-center space-y-2">
            <Trophy className="h-10 w-10 text-primary mx-auto" />
            <div className="font-display font-bold text-xl">Nice work, {firstName}! 💪</div>
            <p className="text-muted-foreground text-sm">
              You crushed today's {home.scheduled?.focus || 'workout'}. Recover well and come back hungry.
            </p>
          </CardContent>
        </Card>
      ) : home?.scheduled ? (
        <Card className="mt-2">
          <CardHeader><CardTitle>Today: {home.scheduled.focus}</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>Week {home.scheduled.week} · Day {home.scheduled.day}. Time to work, {firstName}.</p>
            <Button className="w-full sm:w-auto" onClick={onStartWorkout}>Start today's workout</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="mt-2">
          <CardContent className="py-7 text-center space-y-2">
            <Coffee className="h-10 w-10 text-primary mx-auto" />
            <div className="font-display font-bold text-xl">Rest day</div>
            <p className="text-muted-foreground text-sm">
              Nothing scheduled today — enjoy your recovery. Rest is where the gains are made.
            </p>
          </CardContent>
        </Card>
      )}

      {home?.quote && (
        <Card>
          <CardContent className="py-5">
            <Quote className="h-5 w-5 text-primary mb-2" />
            <blockquote className="text-base italic leading-relaxed">{home.quote.text}</blockquote>
            <div className="text-sm text-muted-foreground mt-2">— {home.quote.author}</div>
          </CardContent>
        </Card>
      )}

      {home?.snack && (
        <Card>
          <CardContent className="py-5">
            <div className="flex items-center gap-1.5 mb-2 text-primary">
              <Apple className="h-4 w-4" />
              <span className="text-sm font-semibold uppercase tracking-wide">Snack of the day</span>
            </div>
            <div className="font-medium">{home.snack.title}</div>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{home.snack.body}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HomeView;
